import { clearStoredAuthState } from "@core/utils/webSessionAuth"

export type PendingServerConnectionSource = "conf" | "lastKnownGood" | "legacyProxy" | "legacyLastServer" | "manual" | "networkScan"
export type BootstrapFallbackReason = "network-scan-suggested" | "network-scan-empty" | "source-unavailable"

export interface BootstrapDiscoveredServer {
  ip: string
  name?: string
}

export interface ServerUpdateInProgressSnapshot {
  inProgress: boolean
  startedAt: number | null
  expiresAt: number | null
  remainingMs: number
}

const PENDING_SOURCE_KEY = "SigmaPendingServerConnectionSource"
const BOOTSTRAP_DISCOVERED_SERVERS_KEY = "SigmaBootstrapDiscoveredServers"
const BOOTSTRAP_FALLBACK_REASON_KEY = "SigmaBootstrapFallbackReason"
const CONNECTION_ERROR_MESSAGE_KEY = "SigmaConnectionErrorMessage"
const SERVER_UPDATE_IN_PROGRESS_KEY = "SigmaServerUpdateInProgress"
const SERVER_UPDATE_DISCONNECT_SEEN_KEY = "SigmaServerUpdateDisconnectSeen"
const SERVER_UPDATE_STATE_CHANGED_EVENT = "sigma-server-update-state-changed"
const DEFAULT_SERVER_UPDATE_GRACE_MS = 5 * 60 * 1000
const BOOTSTRAP_QUERY_KEYS = [
  "bootstrap",
  "bootstrapClearAuthState",
  "bootstrapServerIp",
  "bootstrapServerPcName",
  "bootstrapConnectionSource",
  "bootstrapDiscoveredServers",
  "bootstrapFallbackReason",
]

const VALID_SOURCES: PendingServerConnectionSource[] = ["conf", "lastKnownGood", "legacyProxy", "legacyLastServer", "manual", "networkScan"]
const VALID_BOOTSTRAP_FALLBACK_REASONS: BootstrapFallbackReason[] = ["network-scan-suggested", "network-scan-empty", "source-unavailable"]

function isValidSource(value: string | null): value is PendingServerConnectionSource {
  if (!value) {
    return false
  }

  return VALID_SOURCES.includes(value as PendingServerConnectionSource)
}

function safeStringify(details: Record<string, unknown>) {
  try {
    return JSON.stringify(details)
  } catch (error) {
    return JSON.stringify({
      stringifyError: error instanceof Error ? error.message : String(error),
    })
  }
}

function isValidBootstrapFallbackReason(value: string | null): value is BootstrapFallbackReason {
  if (!value) {
    return false
  }

  return VALID_BOOTSTRAP_FALLBACK_REASONS.includes(value as BootstrapFallbackReason)
}

function normalizeBootstrapDiscoveredServers(value: unknown): BootstrapDiscoveredServer[] {
  if (!Array.isArray(value)) {
    return []
  }

  const normalizedServers: BootstrapDiscoveredServer[] = []
  const seenIps = new Set<string>()

  value.forEach(item => {
    if (!item || typeof item !== "object") {
      return
    }

    const maybeServer = item as { ip?: unknown; name?: unknown }
    if (typeof maybeServer.ip !== "string") {
      return
    }

    const serverIp = maybeServer.ip.trim()
    if (!serverIp || seenIps.has(serverIp)) {
      return
    }

    seenIps.add(serverIp)
    normalizedServers.push({
      ip: serverIp,
      name: typeof maybeServer.name === "string" && maybeServer.name.trim() ? maybeServer.name.trim() : undefined,
    })
  })

  return normalizedServers
}

function writeBootstrapDiscoveredServers(discoveredServers: BootstrapDiscoveredServer[]) {
  if (discoveredServers.length > 0) {
    sessionStorage.setItem(BOOTSTRAP_DISCOVERED_SERVERS_KEY, JSON.stringify(discoveredServers))
    return
  }

  sessionStorage.removeItem(BOOTSTRAP_DISCOVERED_SERVERS_KEY)
}

function writeBootstrapFallbackReason(reason: BootstrapFallbackReason | null) {
  if (reason) {
    sessionStorage.setItem(BOOTSTRAP_FALLBACK_REASON_KEY, reason)
    return
  }

  sessionStorage.removeItem(BOOTSTRAP_FALLBACK_REASON_KEY)
}

function normalizeConnectionErrorMessage(value: string | null): string | null {
  if (typeof value !== "string") {
    return null
  }

  const normalizedMessage = value.trim()
  return normalizedMessage || null
}

export function setConnectionErrorMessage(message: string, details: Record<string, unknown> = {}) {
  const normalizedMessage = normalizeConnectionErrorMessage(message)
  if (!normalizedMessage) {
    clearConnectionErrorMessage()
    return
  }

  sessionStorage.setItem(CONNECTION_ERROR_MESSAGE_KEY, normalizedMessage)
  logRendererBootstrapEvent("connection_error_message_set", {
    message: normalizedMessage,
    ...details,
  })
}

export function getConnectionErrorMessage(): string | null {
  return normalizeConnectionErrorMessage(sessionStorage.getItem(CONNECTION_ERROR_MESSAGE_KEY))
}

export function clearConnectionErrorMessage() {
  sessionStorage.removeItem(CONNECTION_ERROR_MESSAGE_KEY)
  logRendererBootstrapEvent("connection_error_message_cleared")
}

export function logRendererBootstrapEvent(event: string, details: Record<string, unknown> = {}) {
  console.info(`[ServerBootstrap][renderer] ${event} ${safeStringify(details)}`)
}

export function logRendererReload(reason: string, details: Record<string, unknown> = {}) {
  logRendererBootstrapEvent("reload_requested", {
    reason,
    ...details,
  })
}

export function setPendingServerConnectionSource(source: PendingServerConnectionSource, details: Record<string, unknown> = {}) {
  localStorage.setItem(PENDING_SOURCE_KEY, source)
  logRendererBootstrapEvent("pending_source_set", {
    source,
    ...details,
  })
}

export function getPendingServerConnectionSource(): PendingServerConnectionSource | null {
  const value = localStorage.getItem(PENDING_SOURCE_KEY)
  return isValidSource(value) ? value : null
}

export function clearPendingServerConnectionSource() {
  localStorage.removeItem(PENDING_SOURCE_KEY)
  logRendererBootstrapEvent("pending_source_cleared")
}

export function getBootstrapDiscoveredServers(): BootstrapDiscoveredServer[] {
  try {
    const rawValue = sessionStorage.getItem(BOOTSTRAP_DISCOVERED_SERVERS_KEY)
    if (!rawValue) {
      return []
    }

    return normalizeBootstrapDiscoveredServers(JSON.parse(rawValue))
  } catch {
    return []
  }
}

export function getBootstrapFallbackReason(): BootstrapFallbackReason | null {
  try {
    const rawValue = sessionStorage.getItem(BOOTSTRAP_FALLBACK_REASON_KEY)
    return isValidBootstrapFallbackReason(rawValue) ? rawValue : null
  } catch {
    return null
  }
}

export function clearBootstrapFallbackState() {
  sessionStorage.removeItem(BOOTSTRAP_DISCOVERED_SERVERS_KEY)
  sessionStorage.removeItem(BOOTSTRAP_FALLBACK_REASON_KEY)
}

function createEmptyServerUpdateSnapshot(): ServerUpdateInProgressSnapshot {
  return {
    inProgress: false,
    startedAt: null,
    expiresAt: null,
    remainingMs: 0,
  }
}

function dispatchServerUpdateStateChanged(snapshot: ServerUpdateInProgressSnapshot) {
  window.dispatchEvent(
    new CustomEvent(SERVER_UPDATE_STATE_CHANGED_EVENT, {
      detail: snapshot,
    })
  )
}

function clearServerUpdateInProgressStorage() {
  sessionStorage.removeItem(SERVER_UPDATE_IN_PROGRESS_KEY)
  sessionStorage.removeItem(SERVER_UPDATE_DISCONNECT_SEEN_KEY)
}

export function hasServerUpdateDisconnectBeenSeen() {
  return sessionStorage.getItem(SERVER_UPDATE_DISCONNECT_SEEN_KEY) === "1"
}

export function markServerUpdateDisconnectSeen(details: Record<string, unknown> = {}) {
  if (hasServerUpdateDisconnectBeenSeen()) {
    return
  }

  sessionStorage.setItem(SERVER_UPDATE_DISCONNECT_SEEN_KEY, "1")
  logRendererBootstrapEvent("server_update_disconnect_seen", details)
}

export function getServerUpdateInProgressSnapshot(): ServerUpdateInProgressSnapshot {
  const rawValue = sessionStorage.getItem(SERVER_UPDATE_IN_PROGRESS_KEY)
  if (!rawValue) {
    return createEmptyServerUpdateSnapshot()
  }

  try {
    const parsed = JSON.parse(rawValue) as {
      startedAt?: unknown
      expiresAt?: unknown
    }

    if (typeof parsed.startedAt !== "number" || typeof parsed.expiresAt !== "number") {
      clearServerUpdateInProgressStorage()
      return createEmptyServerUpdateSnapshot()
    }

    const remainingMs = parsed.expiresAt - Date.now()
    if (remainingMs <= 0) {
      clearServerUpdateInProgressStorage()
      return createEmptyServerUpdateSnapshot()
    }

    return {
      inProgress: true,
      startedAt: parsed.startedAt,
      expiresAt: parsed.expiresAt,
      remainingMs,
    }
  } catch {
    clearServerUpdateInProgressStorage()
    return createEmptyServerUpdateSnapshot()
  }
}

export function markServerUpdateInProgress(durationMs = DEFAULT_SERVER_UPDATE_GRACE_MS, details: Record<string, unknown> = {}) {
  const startedAt = Date.now()
  const expiresAt = startedAt + Math.max(durationMs, 1)
  const snapshot: ServerUpdateInProgressSnapshot = {
    inProgress: true,
    startedAt,
    expiresAt,
    remainingMs: expiresAt - startedAt,
  }

  sessionStorage.setItem(
    SERVER_UPDATE_IN_PROGRESS_KEY,
    JSON.stringify({
      startedAt,
      expiresAt,
    })
  )
  sessionStorage.removeItem(SERVER_UPDATE_DISCONNECT_SEEN_KEY)

  logRendererBootstrapEvent("server_update_in_progress_set", {
    durationMs,
    expiresAt,
    startedAt,
    ...details,
  })

  dispatchServerUpdateStateChanged(snapshot)

  return snapshot
}

export function clearServerUpdateInProgress(details: Record<string, unknown> = {}) {
  const previousSnapshot = getServerUpdateInProgressSnapshot()

  clearServerUpdateInProgressStorage()

  if (previousSnapshot.inProgress) {
    logRendererBootstrapEvent("server_update_in_progress_cleared", {
      expiresAt: previousSnapshot.expiresAt,
      startedAt: previousSnapshot.startedAt,
      ...details,
    })
  }

  dispatchServerUpdateStateChanged(createEmptyServerUpdateSnapshot())
}

export function subscribeServerUpdateInProgress(listener: () => void) {
  const handleStateChanged = () => {
    listener()
  }

  window.addEventListener(SERVER_UPDATE_STATE_CHANGED_EVENT, handleStateChanged)

  return () => {
    window.removeEventListener(SERVER_UPDATE_STATE_CHANGED_EVENT, handleStateChanged)
  }
}

export function applyWindowBootstrapState() {
  const url = new URL(window.location.href)
  const params = new URLSearchParams(url.search)

  if (params.get("bootstrap") !== "1") {
    return false
  }

  const serverIp = params.get("bootstrapServerIp")?.trim() || null
  const serverPcName = params.get("bootstrapServerPcName")?.trim() || null
  const rawConnectionSource = params.get("bootstrapConnectionSource")
  const connectionSource = isValidSource(rawConnectionSource) ? rawConnectionSource : null
  const rawDiscoveredServers = params.get("bootstrapDiscoveredServers")
  const discoveredServers = rawDiscoveredServers
    ? (() => {
        try {
          return normalizeBootstrapDiscoveredServers(JSON.parse(rawDiscoveredServers))
        } catch {
          return []
        }
      })()
    : []
  const rawFallbackReason = params.get("bootstrapFallbackReason")
  const fallbackReason = isValidBootstrapFallbackReason(rawFallbackReason) ? rawFallbackReason : null
  const clearAuthState = params.get("bootstrapClearAuthState") === "1"

  if (clearAuthState) {
    clearStoredAuthState()
  }

  if (serverIp) {
    localStorage.setItem("SigmaServerIp", serverIp)
  } else {
    localStorage.removeItem("SigmaServerIp")
  }

  if (serverPcName) {
    localStorage.setItem("SigmaServerPcName", serverPcName)
  } else {
    localStorage.removeItem("SigmaServerPcName")
  }

  if (connectionSource) {
    setPendingServerConnectionSource(connectionSource, {
      phase: "window-bootstrap",
      serverIp,
    })
  } else {
    clearPendingServerConnectionSource()
  }

  writeBootstrapDiscoveredServers(discoveredServers)
  writeBootstrapFallbackReason(fallbackReason)

  logRendererBootstrapEvent("window_bootstrap_applied", {
    clearAuthState,
    connectionSource,
    discoveredServerCount: discoveredServers.length,
    fallbackReason,
    serverIp,
    serverPcName,
  })

  BOOTSTRAP_QUERY_KEYS.forEach(key => params.delete(key))

  const nextSearch = params.toString()
  const nextUrl = `${url.pathname}${nextSearch ? `?${nextSearch}` : ""}${url.hash}`
  window.history.replaceState(window.history.state, document.title, nextUrl)

  return true
}

